import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import StockistsTab from './StockistsTab'
import MemberDetail from './MemberDetail'

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatAUD(amount) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount)
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

function timeUntil(iso) {
  const diff = new Date(iso) - new Date()
  if (diff <= 0) return { label: 'Expired', cls: 'expiry-urgent' }
  const hours = Math.floor(diff / 3600000)
  const mins  = Math.floor((diff % 3600000) / 60000)
  if (hours < 1) return { label: `${mins}m`, cls: 'expiry-urgent' }
  if (hours < 4) return { label: `${hours}h ${mins}m`, cls: 'expiry-soon' }
  return { label: `${hours}h ${mins}m`, cls: 'expiry-ok' }
}

// ── Members tab ───────────────────────────────────────────────────────────────

function MembersTab({ onMemberSelect }) {
  const [members,       setMembers]       = useState([])
  const [loading,       setLoading]       = useState(true)
  const [search,        setSearch]        = useState('')
  const [selected,      setSelected]      = useState(null) // member for credit modal
  const [transactions,  setTransactions]  = useState([])
  const [txLoading,     setTxLoading]     = useState(false)
  const [showTxFor,     setShowTxFor]     = useState(null)
  const [createModal,   setCreateModal]   = useState(false)

  const fetchMembers = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('members')
      .select('*')
      .order('name')
    setMembers(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchMembers() }, [fetchMembers])

  async function loadTransactions(memberId) {
    setTxLoading(true)
    setShowTxFor(memberId)
    const { data } = await supabase
      .from('credit_transactions')
      .select('*')
      .eq('member_id', memberId)
      .order('created_at', { ascending: false })
      .limit(50)
    setTransactions(data || [])
    setTxLoading(false)
  }

  const filtered = members.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.email.toLowerCase().includes(search.toLowerCase())
  )

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>

  return (
    <>
      <div className="flex-between" style={{ marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <input
          type="search"
          className="form-input"
          style={{ maxWidth: 280 }}
          placeholder="Search members…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="btn btn-primary btn-sm" onClick={() => setCreateModal(true)}>
          + Invite Member
        </button>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th className="text-right">Balance</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="empty-state">No members found.</td></tr>
              )}
              {filtered.map(m => (
                <React.Fragment key={m.id}>
                  <tr>
                    <td>
                      <button
                        className="btn-link"
                        style={{ fontWeight: 500, textAlign: 'left', padding: 0 }}
                        onClick={() => onMemberSelect(m)}
                      >
                        {m.name}
                      </button>
                    </td>
                    <td className="text-sm text-muted">{m.email}</td>
                    <td>
                      {/* membership_status is vestigial — status is the single source of truth */}
                      {(() => {
                        const BADGE = {
                          active:    { cls: 'active',    label: 'ACTIVE' },
                          lapsed:    { cls: 'lapsed',    label: 'EXPIRING' },
                          closed:    { cls: 'closed',    label: 'CLOSED' },
                          suspended: { cls: 'suspended', label: 'SUSPENDED' },
                        }
                        const b = BADGE[m.status] || { cls: 'inactive', label: m.status || '—' }
                        return <span className={`status-badge dot ${b.cls}`}>{b.label}</span>
                      })()}
                    </td>
                    <td className="text-right" style={{ fontWeight: 600 }}>{formatAUD(m.credit_balance)}</td>
                    <td className="text-sm text-muted">{formatDate(m.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => setSelected(m)}
                        >
                          Add Credit
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => showTxFor === m.id ? setShowTxFor(null) : loadTransactions(m.id)}
                        >
                          {showTxFor === m.id ? 'Hide' : 'History'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {showTxFor === m.id && (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--cream-light)', padding: '1rem' }}>
                        {txLoading ? (
                          <div className="spinner" style={{ margin: '0 auto' }} />
                        ) : transactions.length === 0 ? (
                          <p className="text-sm text-muted">No transactions.</p>
                        ) : (
                          <table>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th className="text-right">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {transactions.map(tx => {
                                const isPos = tx.amount > 0
                                const isNeg = tx.amount < 0
                                const cls   = isPos ? 'amount-positive' : isNeg ? 'amount-negative' : 'amount-neutral'
                                return (
                                  <tr key={tx.id}>
                                    <td className="text-sm text-muted">{formatDate(tx.created_at)}</td>
                                    <td><span className={`type-badge ${tx.type}`}>{tx.type}</span></td>
                                    <td className="text-sm">{tx.description || '—'}</td>
                                    <td className={`text-right ${cls}`}>
                                      {tx.amount === 0 ? '—' : `${isPos ? '+' : ''}${formatAUD(Math.abs(tx.amount))}`}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <AddCreditModal
          member={selected}
          onClose={() => setSelected(null)}
          onSuccess={() => { setSelected(null); fetchMembers() }}
        />
      )}

      {createModal && (
        <CreateMemberModal
          onClose={() => setCreateModal(false)}
          onSuccess={() => { setCreateModal(false); fetchMembers() }}
        />
      )}
    </>
  )
}

// ── Add Credit modal ──────────────────────────────────────────────────────────

function AddCreditModal({ member, onClose, onSuccess }) {
  const [amount,  setAmount]  = useState('20')
  const [desc,    setDesc]    = useState('Monthly membership credit')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    const amt = parseFloat(amount)
    if (!amt || amt <= 0) { setError('Enter a valid amount.'); return }

    setLoading(true)
    const { error } = await supabase.rpc('admin_add_credit', {
      p_member_id:   member.id,
      p_amount:      amt,
      p_description: desc || 'Credit added by admin'
    })
    if (error) setError(error.message)
    else onSuccess()
    setLoading(false)
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal">
        <h2 className="modal-title">Add Credit — {member.name}</h2>
        <p className="text-sm text-muted" style={{ marginBottom: '1rem' }}>
          Current balance: <strong>{formatAUD(member.credit_balance)}</strong>
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Amount (AUD)</label>
            <input
              type="number"
              className="form-input"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              min="0.01"
              step="0.01"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <input
              type="text"
              className="form-input"
              value={desc}
              onChange={e => setDesc(e.target.value)}
              placeholder="Reason for credit"
              maxLength={200}
            />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? 'Adding…' : `Add ${amount ? formatAUD(parseFloat(amount) || 0) : 'Credit'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Create Member modal ───────────────────────────────────────────────────────

function CreateMemberModal({ onClose, onSuccess }) {
  const [name,    setName]    = useState('')
  const [email,   setEmail]   = useState('')
  const [balance, setBalance] = useState('0')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/admin-api', {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          action:        'create_member',
          email,
          name,
          credit_balance: parseFloat(balance) || 0
        })
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Failed to create member.')
      onSuccess()
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal">
        <h2 className="modal-title">Invite New Member</h2>
        <p className="text-sm text-muted" style={{ marginBottom: '1rem' }}>
          Creates a member record and sends a Supabase invitation email so they can set their password.
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input type="text" className="form-input" value={name} onChange={e => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input type="email" className="form-input" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Starting Credit (AUD)</label>
            <input type="number" className="form-input" value={balance} onChange={e => setBalance(e.target.value)} min="0" step="0.01" />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Inviting…' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Pending Codes tab ─────────────────────────────────────────────────────────

function PendingCodesTab() {
  const [codes,   setCodes]   = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  const fetchCodes = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('discount_codes')
      .select('*, members(name, email)')
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
    setCodes(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchCodes() }, [fetchCodes])

  async function handleUpdateCode(codeId, status) {
    setError(null)
    const { error } = await supabase.rpc('admin_update_code_status', {
      p_code_id: codeId,
      p_status:  status
    })
    if (error) setError(error.message)
    else fetchCodes()
  }

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>

  return (
    <>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="flex-between" style={{ marginBottom: '1rem' }}>
        <p className="text-sm text-muted">{codes.length} pending code{codes.length !== 1 ? 's' : ''}</p>
        <button className="btn btn-secondary btn-sm" onClick={fetchCodes}>Refresh</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Member</th>
                <th>Amount</th>
                <th>Generated</th>
                <th>Expires In</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.length === 0 && (
                <tr><td colSpan={6} className="empty-state">No pending codes.</td></tr>
              )}
              {codes.map(code => {
                const exp = timeUntil(code.expires_at)
                return (
                  <tr key={code.id}>
                    <td><span className="code-inline">{code.code}</span></td>
                    <td>
                      <p style={{ fontWeight: 500 }}>{code.members?.name}</p>
                      <p className="text-sm text-muted">{code.members?.email}</p>
                    </td>
                    <td>{formatAUD(code.amount)}</td>
                    <td className="text-sm text-muted">{formatDate(code.created_at)}</td>
                    <td className={exp.cls}>{exp.label}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleUpdateCode(code.id, 'expired')}
                          title="Expire this code now"
                        >
                          Expire
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleUpdateCode(code.id, 'voided')}
                          title="Void this code permanently"
                        >
                          Void
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

// ── Flagged Attempts tab ──────────────────────────────────────────────────────

function FlaggedTab() {
  const [flagged,  setFlagged]  = useState([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    async function fetch() {
      setLoading(true)
      const { data } = await supabase
        .from('discount_codes')
        .select('*, members(name, email)')
        .eq('status', 'invalid_use')
        .order('created_at', { ascending: false })
      setFlagged(data || [])
      setLoading(false)
    }
    fetch()
  }, [])

  // Also load flagged transactions
  const [flaggedTx, setFlaggedTx] = useState([])
  useEffect(() => {
    async function fetch() {
      const { data } = await supabase
        .from('credit_transactions')
        .select('*, members(name, email)')
        .eq('type', 'flagged')
        .order('created_at', { ascending: false })
        .limit(100)
      setFlaggedTx(data || [])
    }
    fetch()
  }, [])

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>

  return (
    <>
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p className="card-title" style={{ color: 'var(--danger)' }}>
          Invalid Code Attempts ({flagged.length})
        </p>
        {flagged.length === 0 ? (
          <div className="empty-state">No invalid use attempts.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Member (Code Owner)</th>
                  <th>Amount</th>
                  <th>Attempted Email</th>
                  <th>Order ID</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {flagged.map(code => (
                  <tr key={code.id}>
                    <td><span className="code-inline">{code.code}</span></td>
                    <td>
                      <p style={{ fontWeight: 500 }}>{code.members?.name}</p>
                      <p className="text-sm text-muted">{code.members?.email}</p>
                    </td>
                    <td>{formatAUD(code.amount)}</td>
                    <td style={{ color: 'var(--danger)', fontWeight: 500 }}>
                      {code.attempted_email || '—'}
                    </td>
                    <td className="text-sm">{code.order_id || '—'}</td>
                    <td className="text-sm text-muted">{formatDate(code.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <p className="card-title">Flagged Transaction Log</p>
        {flaggedTx.length === 0 ? (
          <div className="empty-state">No flagged transactions.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Member</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {flaggedTx.map(tx => (
                  <tr key={tx.id}>
                    <td className="text-sm text-muted">{formatDate(tx.created_at)}</td>
                    <td className="text-sm">
                      <p style={{ fontWeight: 500 }}>{tx.members?.name}</p>
                      <p className="text-muted">{tx.members?.email}</p>
                    </td>
                    <td className="text-sm">{tx.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab() {
  const [stats, setStats] = useState(null)
  const [recent, setRecent] = useState([])

  useEffect(() => {
    async function fetch() {
      const [membersRes, txRes] = await Promise.all([
        supabase.from('members').select('credit_balance, status'),
        supabase
          .from('credit_transactions')
          .select('*, members(name, email)')
          .order('created_at', { ascending: false })
          .limit(10)
      ])

      const members = membersRes.data || []
      setStats({
        total:       members.length,
        active:      members.filter(m => m.status === 'active').length,
        totalCredit: members.reduce((sum, m) => sum + Number(m.credit_balance), 0)
      })
      setRecent(txRes.data || [])
    }
    fetch()
  }, [])

  if (!stats) return <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
        {[
          { label: 'Total Members',    value: stats.total },
          { label: 'Active Members',   value: stats.active },
          { label: 'Total Outstanding Credit', value: formatAUD(stats.totalCredit) }
        ].map(stat => (
          <div key={stat.label} className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <p className="text-sm text-muted" style={{ marginBottom: '0.4rem' }}>{stat.label}</p>
            <p style={{ fontSize: '1.8rem', fontFamily: 'var(--font-serif)', fontWeight: 600 }}>{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="card">
        <p className="card-title">Recent Transactions</p>
        {recent.length === 0 ? (
          <div className="empty-state">No transactions yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Member</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(tx => {
                  const isPos = tx.amount > 0
                  const isNeg = tx.amount < 0
                  const cls   = isPos ? 'amount-positive' : isNeg ? 'amount-negative' : 'amount-neutral'
                  return (
                    <tr key={tx.id}>
                      <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(tx.created_at)}</td>
                      <td>
                        <p style={{ fontWeight: 500 }}>{tx.members?.name}</p>
                        <p className="text-sm text-muted">{tx.members?.email}</p>
                      </td>
                      <td><span className={`type-badge ${tx.type}`}>{tx.type}</span></td>
                      <td className="text-sm">{tx.description || '—'}</td>
                      <td className={`text-right ${cls}`}>
                        {tx.amount === 0 ? '—' : `${isPos ? '+' : ''}${formatAUD(Math.abs(tx.amount))}`}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

// ── System Health tab ─────────────────────────────────────────────────────────

function SystemHealthTab() {
  const [lastPoll,   setLastPoll]   = useState(null)   // { value, updated_at } | null
  const [errors,     setErrors]     = useState([])
  const [loading,    setLoading]    = useState(true)
  const [errCount,   setErrCount]   = useState(null)   // errors in last 7 days
  const [clearing,   setClearing]   = useState(false)
  const [clearMsg,   setClearMsg]   = useState(null)

  const SEVEN_DAYS_AGO = () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()

  const load = useCallback(async () => {
    setLoading(true)
    const [settingRes, errorsRes] = await Promise.all([
      supabase
        .from('system_settings')
        .select('value, updated_at')
        .eq('key', 'last_successful_poll')
        .maybeSingle(),
      supabase
        .from('poll_errors')
        .select('*')
        .gte('created_at', SEVEN_DAYS_AGO())
        .order('created_at', { ascending: false })
        .limit(100)
    ])
    setLastPoll(settingRes.data || null)
    setErrors(errorsRes.data || [])
    setErrCount((errorsRes.data || []).length)
    setLoading(false)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function clearOldErrors() {
    setClearing(true)
    setClearMsg(null)
    const { error, count } = await supabase
      .from('poll_errors')
      .delete({ count: 'exact' })
      .lt('created_at', SEVEN_DAYS_AGO())
    if (error) {
      setClearMsg({ ok: false, text: `Failed to clear: ${error.message}` })
    } else {
      setClearMsg({ ok: true, text: `Cleared ${count ?? 'old'} error${count === 1 ? '' : 's'} older than 7 days.` })
      await load()
    }
    setClearing(false)
  }

  useEffect(() => { load() }, [load])

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>

  // Compute health status
  let ageMinutes = null
  let statusLabel = 'Never run'
  let statusColor = 'var(--danger)'

  if (lastPoll?.value) {
    ageMinutes = (Date.now() - new Date(lastPoll.value).getTime()) / 60000
    if (ageMinutes < 30) {
      statusLabel = 'Healthy'
      statusColor = '#2d7a3a'
    } else if (ageMinutes < 120) {
      statusLabel = 'Delayed'
      statusColor = '#c07000'
    } else {
      statusLabel = 'Stalled'
      statusColor = 'var(--danger)'
    }
  }

  return (
    <>
      {/* Status card */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p className="card-title">Poll-Orders Status</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginTop: '0.75rem' }}>
          <div style={{ textAlign: 'center' }}>
            <p className="text-sm text-muted" style={{ marginBottom: '0.25rem' }}>Status</p>
            <p style={{ fontSize: '1.2rem', fontWeight: 700, color: statusColor }}>{statusLabel}</p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p className="text-sm text-muted" style={{ marginBottom: '0.25rem' }}>Last successful poll</p>
            <p style={{ fontSize: '0.95rem', fontWeight: 600 }}>
              {lastPoll?.value ? formatDate(lastPoll.value) : '—'}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p className="text-sm text-muted" style={{ marginBottom: '0.25rem' }}>Minutes since last run</p>
            <p style={{ fontSize: '1.2rem', fontWeight: 700, color: ageMinutes !== null && ageMinutes > 30 ? 'var(--danger)' : 'inherit' }}>
              {ageMinutes !== null ? Math.round(ageMinutes) : '—'}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p className="text-sm text-muted" style={{ marginBottom: '0.25rem' }}>Errors (last 7 days)</p>
            <p style={{ fontSize: '1.2rem', fontWeight: 700, color: errCount > 0 ? 'var(--danger)' : 'inherit' }}>
              {errCount ?? '—'}
            </p>
          </div>
        </div>

        {ageMinutes !== null && ageMinutes > 30 && (
          <div className="alert alert-error" style={{ marginTop: '1rem' }}>
            Polling has not run in {Math.round(ageMinutes)} minutes. Check Netlify → Functions → poll-orders → Logs.
            {' '}An alert email will be sent at the next daily health check.
          </div>
        )}

        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {clearMsg && (
            <span style={{ fontSize: '0.85rem', color: clearMsg.ok ? '#2d7a3a' : 'var(--danger)' }}>
              {clearMsg.text}
            </span>
          )}
          <button
            className="btn btn-secondary btn-sm"
            onClick={clearOldErrors}
            disabled={clearing}
            title="Delete all poll_errors rows older than 7 days"
          >
            {clearing ? 'Clearing…' : 'Clear old errors'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={load}>Refresh</button>
        </div>
      </div>

      {/* Error log */}
      <div className="card">
        <p className="card-title" style={{ color: errors.length > 0 ? 'var(--danger)' : 'inherit' }}>
          Order Processing Errors — last 7 days ({errors.length})
        </p>
        <p className="text-sm text-muted" style={{ marginBottom: '0.75rem' }}>
          Errors logged by poll-orders.js in the past 7 days. Use "Clear old errors" above to remove
          entries older than 7 days. Successful polls do not appear here.
        </p>

        {errors.length === 0 ? (
          <div className="empty-state">No errors logged — all orders processed cleanly.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Order ID</th>
                  <th>Email</th>
                  <th>Action</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {errors.map(e => (
                  <tr key={e.id}>
                    <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(e.created_at)}</td>
                    <td className="text-sm"><span className="code-inline">{e.order_id || '—'}</span></td>
                    <td className="text-sm text-muted">{e.email || '—'}</td>
                    <td><span className="type-badge flagged">{e.action || '—'}</span></td>
                    <td className="text-sm" style={{ color: 'var(--danger)', maxWidth: 320, wordBreak: 'break-word' }}>
                      {e.error || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

// ── Lapsed Members tab ────────────────────────────────────────────────────────

function LapsedMembersTab({ onMemberSelect }) {
  const [members,   setMembers]   = useState([])
  const [dismissed, setDismissed] = useState(new Set())  // client-side confirmed IDs
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState(null)
  const [working,   setWorking]   = useState(null)        // member id being actioned
  const [closing,   setClosing]   = useState(null)        // member id awaiting close confirm

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('members')
      .select('id, name, email, credit_balance, last_payment_at, lapsed_at, status')
      .eq('status', 'lapsed')
      .order('lapsed_at', { ascending: false })
    if (err) setError(err.message)
    else setMembers(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  async function handleReinstate(memberId) {
    setWorking(memberId)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/admin-api', {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ action: 'reinstate_member', member_id: memberId })
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Failed to reinstate member')
      // Remove from list
      setMembers(prev => prev.filter(m => m.id !== memberId))
    } catch (err) {
      setError(err.message)
    }
    setWorking(null)
  }

  function handleConfirmLapsed(memberId) {
    // Client-side acknowledgement — hides row until next refresh
    setDismissed(prev => new Set([...prev, memberId]))
  }

  async function handleCloseAccount(memberId) {
    setWorking(memberId)
    setClosing(null)
    setError(null)
    const { error: rpcErr } = await supabase.rpc('close_member_account', { p_member_id: memberId })
    if (rpcErr) {
      setError(rpcErr.message)
    } else {
      // Row will now have status='closed'; remove from lapsed list
      setMembers(prev => prev.filter(m => m.id !== memberId))
    }
    setWorking(null)
  }

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>

  const visible = members.filter(m => !dismissed.has(m.id))

  return (
    <>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="flex-between" style={{ marginBottom: '1rem' }}>
        <p className="text-sm text-muted">
          {visible.length} lapsed member{visible.length !== 1 ? 's' : ''}
          {dismissed.size > 0 && ` · ${dismissed.size} confirmed (hidden until refresh)`}
        </p>
        <button className="btn btn-secondary btn-sm" onClick={() => { setDismissed(new Set()); load() }}>
          Refresh
        </button>
      </div>

      {visible.length === 0 && (
        <div className="card">
          <div className="empty-state">No lapsed members — all active.</div>
        </div>
      )}

      {visible.length > 0 && (
        <div className="card">
          <p className="card-title" style={{ color: '#D97706', marginBottom: '0.5rem' }}>
            ⚠ Lapsed Subscriptions ({visible.length})
          </p>
          <p className="text-sm text-muted" style={{ marginBottom: '0.75rem' }}>
            These members have had no subscription payment for {35}+ days. They can still log in
            and see their balance but cannot generate discount codes.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th className="text-right">Balance</th>
                  <th>Last Payment</th>
                  <th>Lapsed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(m => (
                  <tr key={m.id}>
                    <td>
                      <button
                        className="btn-link"
                        style={{ fontWeight: 500, textAlign: 'left', padding: 0 }}
                        onClick={() => onMemberSelect(m)}
                      >
                        {m.name || '—'}
                      </button>
                      <p className="text-sm text-muted">{m.email}</p>
                    </td>
                    <td className="text-right" style={{ fontWeight: 600 }}>{formatAUD(m.credit_balance)}</td>
                    <td className="text-sm text-muted">
                      {m.last_payment_at ? formatDate(m.last_payment_at) : <span style={{ color: '#D97706' }}>Never recorded</span>}
                    </td>
                    <td className="text-sm text-muted">
                      {m.lapsed_at ? formatDate(m.lapsed_at) : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <button
                          className="btn btn-success btn-sm"
                          disabled={working === m.id}
                          onClick={() => handleReinstate(m.id)}
                        >
                          {working === m.id ? 'Working…' : 'Reinstate'}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleConfirmLapsed(m.id)}
                          disabled={working === m.id}
                        >
                          Confirm Lapsed
                        </button>
                        {closing === m.id ? (
                          <>
                            <span className="text-sm" style={{ color: 'var(--danger)', fontWeight: 600 }}>
                              Close & expire ${Number(m.credit_balance).toFixed(0)} credit?
                            </span>
                            <button
                              className="btn btn-danger btn-sm"
                              disabled={working === m.id}
                              onClick={() => handleCloseAccount(m.id)}
                            >
                              {working === m.id ? 'Closing…' : 'Confirm Close'}
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setClosing(null)}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={working === m.id}
                            onClick={() => setClosing(m.id)}
                          >
                            Close Account
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}

// ── Closed Accounts tab ───────────────────────────────────────────────────────

function ClosedAccountsTab({ onMemberSelect }) {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Fetch closed members ordered most-recent-first.
    // credit_balance is already zeroed by close_member_account(); the expiry
    // transaction amount is fetched separately for each member below.
    const { data, error: err } = await supabase
      .from('members')
      .select('id, name, email, last_payment_at, closed_at')
      .eq('status', 'closed')
      .order('closed_at', { ascending: false })
    if (err) { setError(err.message); setLoading(false); return }

    // For each closed member, find the 'expired' transaction to get the amount of
    // credit that was zeroed at close time (most recent expired tx, if any).
    const membersWithExpiry = await Promise.all(
      (data || []).map(async m => {
        const { data: txData } = await supabase
          .from('credit_transactions')
          .select('amount')
          .eq('member_id', m.id)
          .eq('type', 'expired')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
        return { ...m, expired_amount: txData ? Math.abs(txData.amount) : 0 }
      })
    )
    setMembers(membersWithExpiry)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>

  return (
    <>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="flex-between" style={{ marginBottom: '1rem' }}>
        <p className="text-sm text-muted">
          {members.length} closed account{members.length !== 1 ? 's' : ''}
          {' '}· read-only view
        </p>
        <button className="btn btn-secondary btn-sm" onClick={load}>Refresh</button>
      </div>

      <div className="card">
        <p className="card-title" style={{ color: 'var(--muted)', marginBottom: '0.5rem' }}>
          Closed Accounts
        </p>
        <p className="text-sm text-muted" style={{ marginBottom: '0.75rem' }}>
          Accounts closed automatically at 65 days past-due, or manually by an admin.
          Credit was expired at closure. A closed member who resubscribes starts a fresh account.
          {/* TODO: add "date contacted" column once a contact-log feature is built */}
        </p>

        {members.length === 0 ? (
          <div className="empty-state">No closed accounts.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Date Closed</th>
                  <th className="text-right">Credit Expired</th>
                  <th>Last Payment</th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id} style={{ opacity: 0.8 }}>
                    <td>
                      <button
                        className="btn-link"
                        style={{ fontWeight: 500, textAlign: 'left', padding: 0 }}
                        onClick={() => onMemberSelect(m)}
                      >
                        {m.name || '—'}
                      </button>
                      <p className="text-sm text-muted">{m.email}</p>
                    </td>
                    <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>
                      {m.closed_at ? formatDate(m.closed_at) : '—'}
                    </td>
                    <td className="text-right" style={{ color: m.expired_amount > 0 ? 'var(--danger)' : 'inherit' }}>
                      {m.expired_amount > 0 ? `−${formatAUD(m.expired_amount)}` : '—'}
                    </td>
                    <td className="text-sm text-muted">
                      {m.last_payment_at ? formatDate(m.last_payment_at) : <span style={{ color: '#D97706' }}>Never recorded</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

// ── Fraud Alerts tab ──────────────────────────────────────────────────────────

function FraudAlertsTab({ onAlertsChange }) {
  const [alerts,  setAlerts]  = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('fraud_alerts')
      .select('*, members(name)')
      .order('created_at', { ascending: false })
      .limit(200)
    if (err) setError(err.message)
    else setAlerts(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // Keep parent nav badge in sync whenever the alerts array changes
  // (fires on initial load AND after every resolve).
  useEffect(() => {
    if (onAlertsChange) onAlertsChange(alerts.filter(a => !a.resolved).length)
  }, [alerts, onAlertsChange])

  async function handleResolve(id) {
    const { error: err } = await supabase
      .from('fraud_alerts')
      .update({ resolved: true, resolved_at: new Date().toISOString() })
      .eq('id', id)
    if (err) { setError(err.message); return }
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, resolved: true } : a))
  }

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>

  const unresolved = alerts.filter(a => !a.resolved)
  const resolved   = alerts.filter(a => a.resolved)

  return (
    <>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="flex-between" style={{ marginBottom: '1rem' }}>
        <p className="text-sm text-muted">
          {unresolved.length} unresolved · {resolved.length} resolved
        </p>
        <button className="btn btn-secondary btn-sm" onClick={load}>Refresh</button>
      </div>

      {unresolved.length === 0 && resolved.length === 0 && (
        <div className="card">
          <div className="empty-state">No fraud alerts — all clear.</div>
        </div>
      )}

      {unresolved.length > 0 && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <p className="card-title" style={{ color: 'var(--danger)' }}>
            ⚠️ Unresolved Alerts ({unresolved.length})
          </p>
          <p className="text-sm text-muted" style={{ marginBottom: '0.75rem' }}>
            These members used a denomination code at checkout but had insufficient credit.
            <strong> No credit was deducted.</strong> Review and contact the member if needed.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Member</th>
                  <th>Code</th>
                  <th className="text-right">Code Value</th>
                  <th className="text-right">Actual Balance</th>
                  <th>Order ID</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {unresolved.map(a => (
                  <tr key={a.id}>
                    <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(a.created_at)}</td>
                    <td>
                      <p style={{ fontWeight: 500 }}>{a.members?.name || '—'}</p>
                      <p className="text-sm text-muted">{a.email}</p>
                    </td>
                    <td><span className="code-inline">{a.code}</span></td>
                    <td className="text-right" style={{ fontWeight: 600 }}>{formatAUD(a.code_amount)}</td>
                    <td className="text-right" style={{ color: 'var(--danger)', fontWeight: 600 }}>{formatAUD(a.actual_balance)}</td>
                    <td className="text-sm"><span className="code-inline">{a.order_id || '—'}</span></td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleResolve(a.id)}
                      >
                        Mark Resolved
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {resolved.length > 0 && (
        <div className="card">
          <p className="card-title" style={{ color: 'var(--muted)' }}>
            Resolved Alerts ({resolved.length})
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Member</th>
                  <th>Code</th>
                  <th className="text-right">Code Value</th>
                  <th className="text-right">Balance at Time</th>
                  <th>Order ID</th>
                  <th>Resolved</th>
                </tr>
              </thead>
              <tbody>
                {resolved.map(a => (
                  <tr key={a.id} style={{ opacity: 0.6 }}>
                    <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(a.created_at)}</td>
                    <td>
                      <p style={{ fontWeight: 500 }}>{a.members?.name || '—'}</p>
                      <p className="text-sm text-muted">{a.email}</p>
                    </td>
                    <td><span className="code-inline">{a.code}</span></td>
                    <td className="text-right">{formatAUD(a.code_amount)}</td>
                    <td className="text-right">{formatAUD(a.actual_balance)}</td>
                    <td className="text-sm"><span className="code-inline">{a.order_id || '—'}</span></td>
                    <td className="text-sm text-muted">{formatDate(a.resolved_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}

// ── Main Admin Dashboard ──────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',  label: 'Overview' },
  { id: 'members',   label: 'Members' },
  { id: 'lapsed',    label: 'Lapsed Members' },
  { id: 'closed',    label: 'Closed Accounts' },
  { id: 'pending',   label: 'Pending Codes' },
  { id: 'flagged',   label: 'Flagged Attempts' },
  { id: 'fraud',     label: 'Fraud Alerts' },
  { id: 'stockists', label: 'Stockists' },
  { id: 'health',    label: 'System Health' },
]

export default function AdminDashboard({ adminUser }) {
  const [tab,             setTab]             = useState('overview')
  const [fraudAlertCount, setFraudAlertCount] = useState(null)
  const [selectedMember,  setSelectedMember]  = useState(null)  // when set, show MemberDetail instead of tabs

  // Overflow-tab scrolling: show a ‹ / › arrow only when tabs are hidden that side.
  const tabsRef = useRef(null)
  const [tabNav, setTabNav] = useState({ left: false, right: false })
  const updateTabNav = useCallback(() => {
    const el = tabsRef.current
    if (!el) return
    setTabNav({
      left:  el.scrollLeft > 1,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    })
  }, [])
  useEffect(() => {
    updateTabNav()
    window.addEventListener('resize', updateTabNav)
    return () => window.removeEventListener('resize', updateTabNav)
  }, [updateTabNav])
  // Re-measure when the Fraud badge appears/changes (it widens that tab).
  useEffect(() => { updateTabNav() }, [fraudAlertCount, updateTabNav])
  function scrollTabs(dir) {
    tabsRef.current?.scrollBy({ left: dir * 220, behavior: 'smooth' })
  }

  // Lightweight count query on mount so the badge shows before the Fraud tab is opened.
  // FraudAlertsTab calls setFraudAlertCount via onAlertsChange whenever it loads or
  // resolves an alert, keeping the two in sync from a single Supabase source.
  useEffect(() => {
    supabase
      .from('fraud_alerts')
      .select('id', { count: 'exact', head: true })
      .eq('resolved', false)
      .then(({ count }) => { if (count !== null) setFraudAlertCount(count) })
  }, [])

  async function handleSignOut() {
    await supabase.auth.signOut()
  }

  return (
    <div className="layout">
      <header className="topbar">
        <div className="topbar-brand">
          Shepherd's Hut Wines
          <span>Admin</span>
        </div>
        <div className="topbar-actions">
          <span className="topbar-user">{adminUser.email}</span>
          <button
            className="btn btn-secondary btn-sm"
            style={{ borderColor: 'rgba(234,230,219,0.25)', color: 'var(--cream)' }}
            onClick={handleSignOut}
          >
            Sign Out
          </button>
        </div>
      </header>

      <main className="main-content" style={{ maxWidth: 1100 }}>
        <div className="tabs-wrap">
          <button
            type="button"
            className={`tab-arrow tab-arrow-left ${tabNav.left ? '' : 'hidden'}`}
            onClick={() => scrollTabs(-1)}
            aria-label="Show earlier tabs"
            tabIndex={tabNav.left ? 0 : -1}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="tabs" ref={tabsRef} onScroll={updateTabNav}>
            {TABS.map(t => (
              <button
                key={t.id}
                className={`tab-btn ${tab === t.id ? 'active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {t.id === 'fraud' && fraudAlertCount > 0 && (
                  <span className="nav-badge">{fraudAlertCount}</span>
                )}
              </button>
            ))}
          </div>

          <button
            type="button"
            className={`tab-arrow tab-arrow-right ${tabNav.right ? '' : 'hidden'}`}
            onClick={() => scrollTabs(1)}
            aria-label="Show more tabs"
            tabIndex={tabNav.right ? 0 : -1}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {selectedMember ? (
          <MemberDetail
            memberId={selectedMember.id}
            onBack={() => setSelectedMember(null)}
          />
        ) : (
          <>
            {tab === 'overview' && <OverviewTab />}
            {tab === 'members'  && <MembersTab  onMemberSelect={setSelectedMember} />}
            {tab === 'lapsed'   && <LapsedMembersTab  onMemberSelect={setSelectedMember} />}
            {tab === 'closed'   && <ClosedAccountsTab onMemberSelect={setSelectedMember} />}
            {tab === 'pending'  && <PendingCodesTab />}
            {tab === 'flagged'  && <FlaggedTab />}
            {tab === 'fraud'    && <FraudAlertsTab onAlertsChange={setFraudAlertCount} />}
            {tab === 'stockists' && <StockistsTab />}
            {tab === 'health'   && <SystemHealthTab />}
          </>
        )}
      </main>
    </div>
  )
}
